import {
  CreatePointRewardPayload,
  DEFAULT_QR_EXPIRATION_HOURS,
  MAX_POINT_REWARD_COST,
  MAX_QR_EXPIRATION_HOURS,
  MIN_POINT_REWARD_COST,
  MIN_QR_EXPIRATION_HOURS,
  PointReward,
  PointRewardChanges,
  PointRewardType,
} from "../../../models/pointReward";
import { normalizeText } from "../../PointRules/components/pointRuleForm";

/**
 * Estado del formulario de premios de Chispas y su lógica pura: valores iniciales,
 * validación, cambios a enviar al editar y payload del alta. Sin render, así se testea
 * directo. Los números vacíos son null mientras el usuario escribe.
 */
export interface PointRewardFormValues {
  title: string;
  description: string;
  pointsCost: number | null;
  rewardType: PointRewardType | null;
  /** true = ilimitado: el stock que viaja es null, nunca 0. */
  unlimitedStock: boolean;
  stock: number | null;
  /** URL de imagen que va a quedar guardada. null = sin imagen (o quitada al guardar). */
  imageUrl: string | null;
  /** Archivo elegido que todavía no se subió: se sube recién al guardar. */
  imageFile: File | null;
  /** Partner de Beneficios elegido en el desplegable; null = sin partner. */
  partnerId: string | null;
  partnerLocationInfo: string;
  qrExpirationHours: number | null;
  active: boolean;
}

export type PointRewardFormErrorKey =
  | "title"
  | "pointsCost"
  | "rewardType"
  | "stock"
  | "qrExpirationHours";

export type PointRewardFormErrors = Partial<Record<PointRewardFormErrorKey, string>>;

/** Carpeta propia del catálogo de premios en el backend (`rewards/uploads`). */
export const POINT_REWARD_IMAGE_UPLOAD_FOLDER = "rewards";

const COST_RANGE_MESSAGE = `Ingresá un número entero entre ${MIN_POINT_REWARD_COST} y ${MAX_POINT_REWARD_COST}`;
const QR_RANGE_MESSAGE = `Ingresá un número entero entre ${MIN_QR_EXPIRATION_HOURS} y ${MAX_QR_EXPIRATION_HOURS}`;
const STOCK_REQUIRED_MESSAGE = "Ingresá el stock o marcá Stock ilimitado";

const isIntegerInRange = (value: number | null, min: number, max: number): value is number =>
  value !== null && Number.isInteger(value) && value >= min && value <= max;

const isNonNegativeInteger = (value: number | null): value is number =>
  value !== null && Number.isInteger(value) && value >= 0;

export const buildInitialPointRewardFormValues = (
  reward: PointReward | null
): PointRewardFormValues => {
  if (reward === null) {
    return {
      title: "",
      description: "",
      pointsCost: null,
      rewardType: null,
      unlimitedStock: true,
      stock: null,
      imageUrl: null,
      imageFile: null,
      partnerId: null,
      partnerLocationInfo: "",
      qrExpirationHours: DEFAULT_QR_EXPIRATION_HOURS,
      active: true,
    };
  }
  return {
    title: reward.title,
    description: reward.description ?? "",
    pointsCost: reward.pointsCost,
    rewardType: reward.rewardType,
    // null = ilimitado. 0 es agotado, no ilimitado.
    unlimitedStock: reward.stock === null,
    stock: reward.stock,
    imageUrl: reward.imageUrl,
    imageFile: null,
    partnerId: reward.partnerId,
    partnerLocationInfo: reward.partnerLocationInfo ?? "",
    qrExpirationHours: reward.qrExpirationHours,
    active: reward.active,
  };
};

/** Todos los campos son editables por TENANT_ADMIN y SUPER_ADMIN: se valida todo. */
export const validatePointRewardForm = (values: PointRewardFormValues): PointRewardFormErrors => {
  const errors: PointRewardFormErrors = {};

  if (values.title.trim() === "") {
    errors.title = "El premio necesita un título";
  }
  if (!isIntegerInRange(values.pointsCost, MIN_POINT_REWARD_COST, MAX_POINT_REWARD_COST)) {
    errors.pointsCost = COST_RANGE_MESSAGE;
  }
  if (values.rewardType === null) {
    errors.rewardType = "Elegí el tipo de premio";
  }
  if (!values.unlimitedStock && !isNonNegativeInteger(values.stock)) {
    errors.stock = STOCK_REQUIRED_MESSAGE;
  }
  if (!isIntegerInRange(values.qrExpirationHours, MIN_QR_EXPIRATION_HOURS, MAX_QR_EXPIRATION_HOURS)) {
    errors.qrExpirationHours = QR_RANGE_MESSAGE;
  }

  return errors;
};

/** Stock que viaja al backend: null = ilimitado; undefined = limitado sin número (lo frena la validación). */
const resolveStock = (values: PointRewardFormValues): number | null | undefined => {
  if (values.unlimitedStock) return null;
  return values.stock ?? undefined;
};

/**
 * Solo los campos que cambiaron respecto del premio guardado. Los textos vacíos viajan
 * como null. Editar afecta solo canjes futuros: los ya hechos conservan costo y vencimiento.
 */
export const buildPointRewardChanges = (
  reward: PointReward,
  values: PointRewardFormValues
): PointRewardChanges => {
  const changes: PointRewardChanges = {};

  const title = values.title.trim();
  if (title !== reward.title) changes.title = title;

  const description = normalizeText(values.description);
  if (description !== reward.description) changes.description = description;

  if (values.pointsCost !== null && values.pointsCost !== reward.pointsCost) {
    changes.pointsCost = values.pointsCost;
  }
  if (values.rewardType !== null && values.rewardType !== reward.rewardType) {
    changes.rewardType = values.rewardType;
  }

  const stock = resolveStock(values);
  if (stock !== undefined && stock !== reward.stock) changes.stock = stock;

  if (values.imageUrl !== reward.imageUrl) changes.imageUrl = values.imageUrl;

  if (values.partnerId !== reward.partnerId) changes.partnerId = values.partnerId;

  const partnerLocationInfo = normalizeText(values.partnerLocationInfo);
  if (partnerLocationInfo !== reward.partnerLocationInfo) {
    changes.partnerLocationInfo = partnerLocationInfo;
  }

  if (
    values.qrExpirationHours !== null &&
    values.qrExpirationHours !== reward.qrExpirationHours
  ) {
    changes.qrExpirationHours = values.qrExpirationHours;
  }
  if (values.active !== reward.active) changes.active = values.active;

  return changes;
};

/**
 * Payload del alta. Stock, expiración y active siempre viajan; los textos y la imagen
 * solo si tienen valor. Null si faltan datos obligatorios (la validación lo frena antes).
 */
export const buildCreatePointRewardPayload = (
  values: PointRewardFormValues,
  tenantId: string
): CreatePointRewardPayload | null => {
  const stock = resolveStock(values);
  const title = values.title.trim();
  if (
    title === "" ||
    values.pointsCost === null ||
    values.rewardType === null ||
    values.qrExpirationHours === null ||
    stock === undefined
  ) {
    return null;
  }

  const payload: CreatePointRewardPayload = {
    tenantId,
    title,
    pointsCost: values.pointsCost,
    rewardType: values.rewardType,
    stock,
    qrExpirationHours: values.qrExpirationHours,
    active: values.active,
  };

  const description = normalizeText(values.description);
  if (description !== null) payload.description = description;
  if (values.imageUrl !== null) payload.imageUrl = values.imageUrl;
  if (values.partnerId !== null) payload.partnerId = values.partnerId;
  const partnerLocationInfo = normalizeText(values.partnerLocationInfo);
  if (partnerLocationInfo !== null) payload.partnerLocationInfo = partnerLocationInfo;

  return payload;
};
